import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
} from 'react-native';
import { PRIMITIVE_COLORS, SEMANTIC_COLORS } from '@/constants/residents/residentColors';
import { SVG_COMPONENTS } from '@/assets/admin';
import { IMaintenanceListResponse } from '@/store/api/maintenanceApi';
import { DesktopSidePanel } from '@/components/common/admin/desktop/panel/DesktopSidePanel';
import { getTicketStatusInfo } from '@/utils/residents/maintenance';
import { MaintenanceProgressBar } from '@/components/residents/maintenance/shared';
import { MaintenanceImageCarousel } from '@/components/residents/maintenance/shared/MaintenanceImageCarousel';
import { WorkRequestInfoSection } from '@/components/residents/maintenance/shared/WorkRequestInfoSection';
import { ChatCTACard } from '@/components/residents/maintenance/shared/ChatCTACard';

const CloseIcon = SVG_COMPONENTS.CANCEL_X;

interface DesktopMaintenanceDetailPanelProps {
  ticket: IMaintenanceListResponse | null;
  visible: boolean;
  onClose: () => void;
  onStartChat?: () => void;
  chatLoading?: boolean;
}

/**
 * DesktopMaintenanceDetailPanel - Slide-in side panel for ticket details
 * Uses DesktopSidePanel for proper modal behavior with dark theme styling
 */
export const DesktopMaintenanceDetailPanel: React.FC<DesktopMaintenanceDetailPanelProps> = ({
  ticket,
  visible,
  onClose,
  onStartChat,
  chatLoading = false,
}) => {
  if (!ticket) return null;

  // Get status info
  const statusInfo = getTicketStatusInfo(ticket);
  
  // Get file attachments from ticket
  const images: string[] = ticket.files?.map(f => f.fileUrl).filter(Boolean) || [];

  // Title from description
  const title = ticket.maintenanceDescription?.substring(0, 50) || `Request #${ticket.maintenanceRequestId}`;

  return (
    <DesktopSidePanel
      visible={visible}
      onClose={onClose}
      width={400}
    >
      <View style={styles.panelContent}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.headerTitle}>Details</Text>
          <TouchableOpacity
            style={styles.closeButton}
            onPress={onClose}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <CloseIcon width={16} height={16} color={PRIMITIVE_COLORS.gray400} />
          </TouchableOpacity>
        </View>

        <ScrollView
          style={styles.content}
          contentContainerStyle={styles.contentContainer}
          showsVerticalScrollIndicator={false}
        >
          {/* Status Section */}
          <View style={styles.statusSection}>
            <View style={styles.statusRow}>
              <Text style={styles.sectionTitle}>Status</Text>
              <Text style={styles.statusLabel}>{statusInfo.label}</Text>
            </View>
            <MaintenanceProgressBar progress={statusInfo.progress} variant="desktop" />
          </View>

          {/* Divider */}
          <View style={styles.divider} />

          {/* Problem Section */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Problem</Text>
            
            {/* Image Carousel using shared component */}
            <MaintenanceImageCarousel images={images} variant="desktop" />

            {/* Problem Description */}
            <View style={styles.problemDescription}>
              <Text style={styles.problemTitle}>{title}</Text>
              {ticket.maintenanceDescription && (
                <Text style={styles.problemText}>
                  {ticket.maintenanceDescription}
                </Text>
              )}
            </View>
          </View>

          {/* Work Request Information using shared component */}
          <WorkRequestInfoSection ticket={ticket} variant="desktop" />

          {/* Chat CTA Card using shared component */}
          <ChatCTACard onPress={onStartChat} variant="desktop" loading={chatLoading} />
        </ScrollView>
      </View>
    </DesktopSidePanel>
  );
};

const styles = StyleSheet.create({
  // Panel Content - dark theme override
  panelContent: {
    flex: 1,
    backgroundColor: PRIMITIVE_COLORS.gray900,
  },

  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: PRIMITIVE_COLORS.gray700,
    backgroundColor: PRIMITIVE_COLORS.gray900,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: SEMANTIC_COLORS.text.primary,
    lineHeight: 36,
  },
  closeButton: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Content
  content: {
    flex: 1,
    backgroundColor: PRIMITIVE_COLORS.gray900,
  },
  contentContainer: {
    paddingBottom: 32,
  },

  // Sections
  statusSection: {
    paddingHorizontal: 24,
    paddingVertical: 24,
    gap: 16,
    borderBottomWidth: 1,
    borderBottomColor: PRIMITIVE_COLORS.gray700,
  },
  section: {
    paddingHorizontal: 24,
    paddingVertical: 24,
    gap: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '400',
    color: SEMANTIC_COLORS.text.primary,
    lineHeight: 24,
  },

  // Divider
  divider: {
    height: 1,
    backgroundColor: PRIMITIVE_COLORS.gray700,
  },

  // Status
  statusRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statusLabel: {
    fontSize: 16,
    fontWeight: '400',
    color: PRIMITIVE_COLORS.gray300,
    lineHeight: 24,
  },

  // Problem
  problemDescription: {
    gap: 4,
  },
  problemTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: SEMANTIC_COLORS.text.primary,
    lineHeight: 21,
  },
  problemText: {
    fontSize: 14,
    fontWeight: '400',
    color: PRIMITIVE_COLORS.gray300,
    lineHeight: 21,
  },
});

export default DesktopMaintenanceDetailPanel;
